import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.heat';

interface HeatmapLayerProps {
  /** Array of [lat, lng, intensity?] tuples */
  points: Array<[number, number] | [number, number, number]>;
  radius?: number;
  blur?: number;
  max?: number;
  minOpacity?: number;
  gradient?: Record<number, string>;
}

/**
 * Renders a leaflet.heat density heatmap inside a react-leaflet MapContainer.
 * Must be mounted as a child of <MapContainer>.
 */
export default function HeatmapLayer({
  points,
  radius = 22,
  blur = 18,
  max = 1.0,
  minOpacity = 0.3,
  gradient = { 0.0: '#0ea5e9', 0.4: '#22d3ee', 0.6: '#facc15', 0.8: '#f97316', 1.0: '#ef4444' },
}: HeatmapLayerProps) {
  const map = useMap();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const layerRef = useRef<any>(null);

  useEffect(() => {
    if (!map || points.length === 0) return;

    // Remove previous layer
    if (layerRef.current) {
      map.removeLayer(layerRef.current);
      layerRef.current = null;
    }

    // leaflet.heat extends L with heatLayer() at runtime
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const heat = (L as any).heatLayer(points, { radius, blur, max, minOpacity, gradient });
    heat.addTo(map);
    layerRef.current = heat;

    return () => {
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
    };
  }, [map, points, radius, blur, max, gradient]);

  return null;
}
